
import BarcodeReader from 'react-barcode-reader'
import { useState, useCallback, useRef } from "react";
import { Button, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import config from "@/config";
import {
    directBMQrHandlerApi,
    handleBoardMissionsBarcodeActions,
    handleBoardMissionsBarcodeActionsApi,
    moveBoardMissionToDoneApi,
    tryMoveBoardMissionToDoneApi,
} from "@/services/api-service/production-floor/production-floor-endpoints";
import { moveOrderToDoneApi, getDocumentMailDataApi } from "@/services/api-service/generic-doc/documents-api";
import { ModalType, QrCodeAction } from "@/enums";
import { EPreferences } from "@/enums/prefernces";
import { usePrintHousePreferences } from "@/hooks/use-print-house-preferences";
import { normalizeBoardMissionBarcode } from "@/widgets/production-floor/utils/board-mission-barcode.utils";
import {
    markDirectQrProcessingRow,
    clearDirectQrProcessingRow,
    directQrProcessingRowsState,
} from "@/widgets/production-floor/state/direct-qr-processing-rows";
import type { IDirectBmQrHandlerResponse } from "@/widgets/production-floor/types/direct-bm-qr-handler.types";
import { QR_CODE_LISTENER_FROM } from "@/hooks/use-qr-code-listener";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { boardMissionsDetailsState, selectedMissionsState } from "@/widgets/production-floor/state/boards";
import { boardMissionsStationsState } from "@/widgets/production-floor/views/board-missions-view/stations/state";
import { boardMissionModalState } from "@/widgets/production-floor/state/board-mission-modal";
import { useGomakeAxios, useSnackBar } from "@/hooks";
import { userState } from "@/store";
import { useRouter } from "next/router";
import { useTranslation } from 'react-i18next';
import { GoMakeAlertModal, QRErrorModal } from "../modal";
import {
    ThreeOptionsModal,
    GoMakeDeleteModal,
    GoMakeAutoComplate,
} from "@/components";
import { useStorageList } from "@/hooks/use-storage-list";
import {
    PrintPackingSlipModalWidget
} from "@/widgets/production-floor/views/board-missions-view/navigation-buttons/print-packing-slip-modal-widget";
import { useIsMobile } from "@/hooks/use-responsive";
import { useFrontDeskEmailIntegration } from '@/pages-components/quotes/hooks/use-frontdesk-email-integration';
import { FrontDeskEmailComposerWrapper } from '@/pages-components/quotes/components/frontdesk-email-composer-wrapper';
import { DOCUMENT_TYPE } from '@/pages-components/quotes/enums';
import { printHouseHasFrontDeskFeature } from "@/store/print-house-profile";
import { contactResolutionService, conversationComposerService } from "@/services/frontdesk";
import { chatwootSelectedAccountAtom, chatwootProfileAtom } from "@/store/frontdesk/atoms/chatwoot.atoms";
import { useChatwootProfile } from "@/widgets/frontdesk/providers/chatwoot/hooks/use-chatwoot-profile";
import { useChatwootInboxes } from "@/widgets/frontdesk/providers/chatwoot/hooks/use-chatwoot-inboxes";
import { ChannelType } from "@/services/frontdesk/api/chatwoot/types/inboxes.types";
import type { ContactCreatePayload } from "@/services/frontdesk/api/chatwoot/types/contacts.types";
import { getCapitalizedNameFromEmail } from "@/widgets/frontdesk/providers/chatwoot/utils/contact-utils";
import { convertAttachmentsToFiles } from "@/widgets/frontdesk/providers/chatwoot/utils/attachment.utils";
import type { AttachmentSource } from "@/widgets/frontdesk/providers/chatwoot/types/attachment.types";
import { EmailAttachment } from "@/widgets/frontdesk/providers/email/types/email.types";
import { getFileExtension, getMimeTypeFromExtension } from "@/widgets/frontdesk/providers/chatwoot/utils/attachment.utils";

interface IProps {
    qrCodeListenerFrom: QR_CODE_LISTENER_FROM;
    onIsLastStationDetect?: () => void;
}

type MoveToReadyModalMode =
    | null
    | "hasJobs"
    | "withMessage"
    | "frontDeskConfirm";

/** Match next.config/env; quotes and casing from .env ignored. */
const normalizeEnvironmentType = () =>
    (config.enviroment_Type ?? "")
        .replace(/^["']+|["']+$/g, "")
        .trim()
        .toLowerCase();

/** Manual QR/barcode widget: any env except explicit `production`. */
const isNonProductionEnvironmentType = () =>
    normalizeEnvironmentType() !== "production";

const buildOrderMoveToDoneUrl = (orderData: {
    orderId?: string;
    clientId?: string;
    orderNumber?: string;
    quantity?: number | string | null;
    totalOrderQuantity?: number | string | null;
}) => {
    const searchParams = new URLSearchParams();
    if (orderData.orderId) {
        searchParams.set("orderId", String(orderData.orderId));
    }
    if (orderData.clientId) {
        searchParams.set("clientId", String(orderData.clientId));
    }
    if (orderData.orderNumber) {
        searchParams.set("orderNumber", String(orderData.orderNumber));
        searchParams.set("finalPatternSearch", String(orderData.orderNumber));
    }
    const totalQuantity =
        orderData.totalOrderQuantity ?? orderData.quantity;
    if (totalQuantity != null && totalQuantity !== "") {
        searchParams.set("totalOrderQuantity", String(totalQuantity));
    }
    searchParams.set("isOpenMoveToDoneModal", "true");
    return `/orders?${searchParams.toString()}`;
};

const QrCodeReader = (props: IProps) => {
    const allowManualQrEntry = isNonProductionEnvironmentType();
    const { t } = useTranslation();
    const { alertSuccessUpdate, alertFault, alertFaultUpdate } = useSnackBar();
    const { CheckPreferenceValue } = usePrintHousePreferences();
    const user = useRecoilValue<any>(userState);
    const { callApi } = useGomakeAxios();
    const setDirectQrProcessingRows = useSetRecoilState(directQrProcessingRowsState);
    const isQrProcessingRef = useRef(false);
    const pendingDirectQrProcessingRef = useRef<{
        boardMissionId: string;
        productType: string;
    } | null>(null);
    const router = useRouter();
    const isMobile = useIsMobile();
    const hasFrontDeskFeature = useRecoilValue(printHouseHasFrontDeskFeature);
    const [selectedMission, setSelectedMission] = useRecoilState<any>(selectedMissionsState);
    const [boardMissionModal, setBoardMissionModal] = useRecoilState(boardMissionModalState);
    const [stations, setStations] = useRecoilState(boardMissionsStationsState);
    const [myState, setMyState] = useState("helll");
    const [devManualBarcode, setDevManualBarcode] = useState("");
    const [devManualQrMinimized, setDevManualQrMinimized] = useState(false);
    const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
    const [deliveryQRData, setDeliveryQRData] = useState<any>(null);
    const [openMarkReadyModal, setOpenMarkReadyModal] = useState<boolean>(false);
    const [openPackagesModal, setOpenPackagesModal] = useState<boolean>(false);
    const [moveToReadyMode, setMoveToReadyMode] =
        useState<MoveToReadyModalMode>(null);
    const {
        storageList,
        inputValue,
        defaultValue,
        addPrintHouseJobsStorage,
        handleStorageChange,
        setInputValue,
    } = useStorageList();
    const [selectedJobQuantity, setSelectedJobQuantity] = useState<number | null>(null);
    const [isQRErrorModalOpen, setIsQRErrorModalOpen] = useState<boolean>(false);
    const [qrErrorMessage, setQrErrorMessage] = useState<string>("");
    const [qrErrorKey, setQrErrorKey] = useState<string>("");
    const [boardMissions, setBoardMissions] = useRecoilState(
        boardMissionsDetailsState,
    );
    const openBoardMissionModal = (missionId?: string, missionProductType?: string) => {
        setBoardMissionModal({
            isOpen: true,
            boardMissionsId: missionId || null,
            productType: missionProductType || "",
            step: "stations",
        });
    };
    const handleBoardMissionsBarcodeActionsFunction = async (barcode: string, data: any) => {

        return new Promise(async (resolve, reject) => {
            barcode = normalizeBoardMissionBarcode(barcode);
            const callBack = (res) => {
                if (res?.data?.success) {
                    if (res?.data?.isLastStation) {
                        //isOpenDoneModal.open()
                        if (props?.onIsLastStationDetect) {
                            props.onIsLastStationDetect();
                        }
                    }
                    alertSuccessUpdate();
                }
                else {
                    if (res?.data?.errorMessage && res?.data?.errorMessage !== "") {
                        setQrErrorMessage(res.data.errorMessage);
                        setQrErrorKey(res.data.errorMessage);
                        setIsQRErrorModalOpen(true);
                    } else {
                        alertFault(t("productionFloor.qrReadError"));
                    }
                }
                if (res?.data?.moveToDoneResponse) {
                    const boardMissionActionId = res?.data?.currentActionId;
                    const currentActionId = res?.data?.moveToDoneResponse?.currentStationId;
                    setStations(
                        stations.map((station) => {
                            if (station?.boardMissionActionId === boardMissionActionId) {
                                return {
                                    ...station,
                                    isDone: true,
                                    boardMissionActionTimer: {
                                        isTimerRunning: false,
                                        totalRunningTime: 0,
                                    },
                                };
                            }
                            return {
                                ...station,
                            };
                        }),
                    );
                    setBoardMissions({
                        ...boardMissions,
                        currentStation: {
                            ...boardMissions.currentStation,
                            boardMissionActionTimer: {
                                ...boardMissions.currentStation?.boardMissionActionTimer,
                                isTimerRunning: false,
                                totalRunningTime: 0,
                            },
                        },
                        currentBoardMissionActionId: currentActionId,
                    });
                }


                resolve(true)
            };
            await handleBoardMissionsBarcodeActions(callApi, callBack, {
                barcode
            });
        })
    };

    const handleDeliveryQRCode = (qrData: any) => {
        if (deliveryQRData && deliveryQRData.orderId === qrData.orderId) {
            // User has already seen the warning for this QR code, proceed to order page
            const url = `/orders?orderId=${qrData?.orderId}&clientId=${qrData?.clientId}&orderNumber=${qrData?.orderNumber}&isComeFromQR=true`;
            router.push(url);
        } else {
            // First time scanning this delivery QR code, show warning
            setDeliveryQRData(qrData);
            setIsDeliveryModalOpen(true);
        }
    };

    const clearDirectQrProcessingForMission = useCallback(() => {
        const pending = pendingDirectQrProcessingRef.current;
        const missionId =
            pending?.boardMissionId ??
            selectedMission?.id ??
            selectedMission?.boardMissionId;
        const productType =
            pending?.productType ?? selectedMission?.productType ?? "";

        if (missionId) {
            clearDirectQrProcessingRow(
                setDirectQrProcessingRows,
                String(missionId),
                productType,
            );
        }
        pendingDirectQrProcessingRef.current = null;
    }, [selectedMission, setDirectQrProcessingRows]);

    const closeMoveToReadyModal = useCallback(() => {
        clearDirectQrProcessingForMission();
        setMoveToReadyMode(null);
    }, [clearDirectQrProcessingForMission]);

    const runTryMoveAndOpenMoveToReadyModal = useCallback(
        async (scanPayload: any) => {
            setSelectedJobQuantity(scanPayload?.quantity ?? null);
            const boardMissionId =
                scanPayload?.boardMissionId ?? scanPayload?.id ?? null;

            if (
                scanPayload?.qrCodeAction === QrCodeAction.OrderToReady &&
                !boardMissionId
            ) {
                setMoveToReadyMode(
                    hasFrontDeskFeature ? "frontDeskConfirm" : "withMessage",
                );
                return;
            }

            if (!boardMissionId) {
                alertFault(t("productionFloor.qrReadError"));
                return;
            }

            await tryMoveBoardMissionToDoneApi(
                callApi,
                (tryRes) => {
                    if (!tryRes?.success) {
                        alertFaultUpdate();
                        return;
                    }
                    const hasRelatedJobs = Boolean(tryRes.data);
                    if (hasRelatedJobs) {
                        setMoveToReadyMode("hasJobs");
                    } else if (hasFrontDeskFeature) {
                        setMoveToReadyMode("frontDeskConfirm");
                    } else {
                        setMoveToReadyMode("withMessage");
                    }
                },
                { boardMissionId: String(boardMissionId) },
            );
        },
        [alertFault, alertFaultUpdate, callApi, hasFrontDeskFeature, t],
    );

    const renderMoveToReadyStorageField = () => (
        <div style={{ width: "100%", minWidth: 300 }}>
            <GoMakeAutoComplate
                options={
                    storageList?.find(
                        (option: any) => option.storage === inputValue,
                    ) || inputValue === ""
                        ? storageList
                        : [
                            {
                                storage: `Add "${inputValue}" as a new storage`,
                                id: "new",
                            },
                            ...storageList,
                        ]
                }
                placeholder={t("boardMissions.selectStorage")}
                key={String(
                    defaultValue?.id ?? defaultValue?.storage ?? "storage",
                )}
                style={{ width: "100%", border: "none", height: 45 }}
                getOptionLabel={(option: any) => option?.storage}
                value={defaultValue}
                onChange={(e: any, value: any) => {
                    if (value?.id === "new") {
                        addPrintHouseJobsStorage(inputValue);
                        setInputValue("");
                    } else {
                        handleStorageChange(e, value);
                        setInputValue("");
                    }
                }}
                onInputChange={(event: any, newInputValue: string) => {
                    setInputValue(newInputValue);
                }}
            />
        </div>
    );

    const showDirectQrError = (message?: string) => {
        if (message && message !== "") {
            setQrErrorMessage(message);
            setQrErrorKey(message);
            setIsQRErrorModalOpen(true);
        } else {
            alertFault(t("productionFloor.qrReadError"));
        }
    };

    const handleDirectBoardMissionQr = async (rawBarcode: string) => {
        if (isQrProcessingRef.current) {
            return;
        }
        isQrProcessingRef.current = true;
        try {
            const normalizedBarcode = normalizeBoardMissionBarcode(rawBarcode);
            await directBMQrHandlerApi(callApi, (res) => {
                if (!res?.success) {
                    const httpErrorMessage =
                        (res?.data as IDirectBmQrHandlerResponse | undefined)
                            ?.errorMessage ||
                        (typeof res?.data === "string" ? res.data : undefined);
                    showDirectQrError(httpErrorMessage);
                    return;
                }

                const payload = (res?.data ?? res) as IDirectBmQrHandlerResponse;

                if (!payload?.success) {
                    showDirectQrError(payload?.errorMessage);
                    return;
                }

                const boardMissionId = payload.moveToDoneResponse?.boardMissionId;
                const productType = payload.moveToDoneResponse?.productType;

                if (payload.isLastStation && props.onIsLastStationDetect) {
                    props.onIsLastStationDetect();
                }

                if (!boardMissionId) {
                    alertSuccessUpdate();
                    return;
                }

                markDirectQrProcessingRow(
                    setDirectQrProcessingRows,
                    String(boardMissionId),
                    productType != null ? String(productType) : ""
                );
                pendingDirectQrProcessingRef.current = {
                    boardMissionId: String(boardMissionId),
                    productType: productType != null ? String(productType) : "",
                };
                setSelectedMission((prev: any) => ({
                    ...prev,
                    boardMissionId: boardMissionId,
                    productType: productType,
                }));
                

                alertSuccessUpdate();
            }, normalizedBarcode);
        } finally {
            isQrProcessingRef.current = false;
        }
    };

    const handleBoardMissionsQRCodeAction = async (barcode: string) => {
        return new Promise(async (resolve, reject) => {
            if (barcode && barcode.startsWith("https")) {
                window.open(barcode, "_blank");
                return;
            }
            barcode = normalizeBoardMissionBarcode(barcode);
            const callBack = async (res) => {
                setSelectedMission(res?.data);
                if (res?.success) {
                    if (props.qrCodeListenerFrom === QR_CODE_LISTENER_FROM.PRODUCTION_FLOOR && router.pathname === "/production-floor") {
                        const currentBoardMissionId = boardMissionModal.boardMissionsId;
                        if (res?.data?.qrCodeAction === QrCodeAction.START) {
                            // If a different job START is scanned while a job modal is open, navigate to the new job
                            if (currentBoardMissionId && String(currentBoardMissionId) !== String(res?.data?.boardMissionId)) {
                                openBoardMissionModal(res?.data?.boardMissionId, res?.data?.productType);
                            } else {
                                if (stations?.length > 0) {
                                    await handleBoardMissionsBarcodeActionsFunction(barcode, res?.data);
                                } else {
                                    openBoardMissionModal(res?.data?.boardMissionId, res?.data?.productType);
                                }
                            }
                        } else if (res?.data?.qrCodeAction === QrCodeAction.FINISH) {
                            if (currentBoardMissionId && String(currentBoardMissionId) !== String(res?.data?.boardMissionId)) {
                                openBoardMissionModal(res?.data?.boardMissionId, res?.data?.productType);
                            } else {
                                if (stations?.length > 0) {
                                    await handleBoardMissionsBarcodeActionsFunction(barcode, res?.data);
                                } else {
                                    openBoardMissionModal(res?.data?.boardMissionId, res?.data?.productType);
                                }
                            }

                        } else if (res?.data?.qrCodeAction === QrCodeAction.DELIVERY) {
                            await runTryMoveAndOpenMoveToReadyModal(res?.data);
                            // const url = `/orders?orderId=${res?.data?.orderId}&clientId=${res?.data?.clientId}&orderNumber=${res?.data?.orderNumber}&isComeFromQR=true`;
                            // router.push(url);
                        } else if (res?.data?.qrCodeAction === QrCodeAction.OrderToReady) {
                            window.open(buildOrderMoveToDoneUrl(res?.data), "_blank");
                        }
                    }
                    else {
                        if (res?.data?.qrCodeAction === QrCodeAction.START || res?.data?.qrCodeAction === QrCodeAction.FINISH) {
                            openBoardMissionModal(res?.data?.boardMissionId, res?.data?.productType);
                            router.push("/production-floor");
                        }
                        else {
                            if (res?.data?.qrCodeAction === QrCodeAction.DELIVERY) {
                                await runTryMoveAndOpenMoveToReadyModal(res?.data);
                                // const url = `/orders?orderId=${res?.data?.orderId}&clientId=${res?.data?.clientId}&orderNumber=${res?.data?.orderNumber}&isComeFromQR=true`;
                                // router.push(url);
                            } else if (res?.data?.qrCodeAction === QrCodeAction.OrderToReady) {
                                window.open(buildOrderMoveToDoneUrl(res?.data), "_blank");
                            }
                        }
                    }
                } else {
                    if (res?.errorMessage && res?.errorMessage !== "") {
                        setQrErrorMessage(res.errorMessage);
                        setQrErrorKey(res.errorMessage);
                        setIsQRErrorModalOpen(true);
                    } else {
                        alertFault(t("productionFloor.qrReadError"));
                    }
                }
                resolve(true);
            };
            await handleBoardMissionsBarcodeActionsApi(callApi, callBack, {
                barcode
            });
        });
    };
    const handleScan = async (data: string) => {
        if (data && data.startsWith("https")) {
            window.open(data, "_blank");
            return;
        }

        const isDirectQrEnabled =
            CheckPreferenceValue(EPreferences.USE_DIRECT_BOARDMISSION_QR_HANDLER) ===
                true && user?.isUseDirectQrHandler === true;

        if (isDirectQrEnabled) {
            const barcodeAction = Number(
                normalizeBoardMissionBarcode(data).split("-").pop()
            );
            const useLegacyQrHandler =
                barcodeAction === QrCodeAction.DELIVERY ||
                barcodeAction === QrCodeAction.OrderToReady;

            if (useLegacyQrHandler) {
                await handleBoardMissionsQRCodeAction(data);
            } else {
                await handleDirectBoardMissionQr(data);
            }
            return;
        }

        await handleBoardMissionsQRCodeAction(data);
    };

    const submitManualDevBarcode = async () => {
        const trimmed = devManualBarcode.trim();
        if (!trimmed || !allowManualQrEntry) return;
        await handleScan(trimmed);
        setDevManualBarcode("");
    };
    const handleError = (err) => {
        console.error(err)
    }

    const handleModalConfirm = () => {
        setIsDeliveryModalOpen(false);
        if (deliveryQRData) {
            const url = `/orders?orderId=${deliveryQRData?.orderId}&clientId=${deliveryQRData?.clientId}&orderNumber=${deliveryQRData?.orderNumber}&isComeFromQR=true`;
            router.push(url);
        }
    };

    const {
        isEmailComposerOpen,
        emailData,
        openEmailComposer,
        closeEmailComposer,
    } = useFrontDeskEmailIntegration({ documentType: DOCUMENT_TYPE.boardMission });

    // FrontDesk email sending setup for programmatic sending
    const selectedAccountId = useRecoilValue(chatwootSelectedAccountAtom);
    const setSelectedAccountId = useSetRecoilState(chatwootSelectedAccountAtom);
    const currentUser = useRecoilValue(chatwootProfileAtom);
    const { profile: profileUser, fetchProfile } = useChatwootProfile();
    const { inboxes } = useChatwootInboxes();

    // Helper function to send email programmatically without opening modal
    const sendEmailProgrammatically = useCallback(
        async (
            boardMissionId: string | number,
            contactEmail?: string,
            contactPhone?: string,
            contactName?: string,
            customerId?: string | number,
            customerName?: string
        ): Promise<boolean> => {
            if (!hasFrontDeskFeature) {
                return false;
            }

            try {
                // 1. Get email data from API
                let emailData: any = null;
                await new Promise<void>((resolve) => {
                    getDocumentMailDataApi(
                        callApi,
                        (res) => {
                            if (res?.success && res?.data) {
                                emailData = res.data;
                            }
                            resolve();
                        },
                        {
                            documentType: DOCUMENT_TYPE.boardMission,
                            document: {
                                boardMissionId,
                                ...(contactEmail && { contactEmail }),
                                ...(contactPhone && { contactPhone }),
                                messageType: 0,
                                sendMailIfNotVerifiedAgent: true,
                                sendWithInvoices: false,
                                ...(customerId && { customerId }),
                                ...(customerName && { customerName }),
                                ...(contactName && { contactName }),
                            },
                        }
                    );
                });

                if (!emailData) {
                    return false;
                }
                // 2. Resolve account ID
                const extractAccountId = (profile: any) => {
                    if (!profile) return null;
                    return (
                        profile.account_id ||
                        profile.accountId ||
                        (profile.meta && (profile.meta.account_id || profile.meta.accountId)) ||
                        null
                    );
                };

                let resolvedAccountId =
                    typeof selectedAccountId === "number" ? selectedAccountId : null;
                if (!resolvedAccountId) {
                    resolvedAccountId =
                        extractAccountId(currentUser) ?? extractAccountId(profileUser);
                }
                if (!resolvedAccountId) {
                    const refreshedProfile = await fetchProfile();
                    resolvedAccountId = extractAccountId(refreshedProfile);
                }
                if (resolvedAccountId) {
                    setSelectedAccountId(resolvedAccountId);
                }

                if (!resolvedAccountId) {
                    return false;
                }

                const accountId = Number(resolvedAccountId);
                if (Number.isNaN(accountId)) {
                    return false;
                }

                // 3. Get sender inbox (use first email inbox)
                const emailInboxes = inboxes?.filter(
                    (inbox) => inbox.channel_type === ChannelType.EMAIL
                ) || [];
                if (emailInboxes.length === 0) {
                    return false;
                }
                const senderInbox = emailInboxes[0];

                // 4. Prepare email data from API response
                // Handle tos array - ensure it's always an array
                const toEmails = Array.isArray(emailData.tos)
                    ? emailData.tos.filter((email: string) => email && email.trim())
                    : emailData.tos && typeof emailData.tos === "string"
                        ? [emailData.tos.trim()].filter(Boolean)
                        : [];

                // Handle cc - can be null, array, or string
                const ccEmails = emailData.cc === null || emailData.cc === undefined
                    ? []
                    : Array.isArray(emailData.cc)
                        ? emailData.cc.filter((email: string) => email && email.trim())
                        : typeof emailData.cc === "string"
                            ? [emailData.cc.trim()].filter(Boolean)
                            : [];

                // Handle bcc - can be null, array, or string
                const bccEmails = emailData.bcc === null || emailData.bcc === undefined
                    ? []
                    : Array.isArray(emailData.bcc)
                        ? emailData.bcc.filter((email: string) => email && email.trim())
                        : typeof emailData.bcc === "string"
                            ? [emailData.bcc.trim()].filter(Boolean)
                            : [];
                const messageContent = emailData.body || emailData.html || emailData.text || "";
                const subject = emailData.subject || "";

                if (toEmails.length === 0) {
                    return false;
                }

                // 5. Convert attachments
                const mapToAttachmentSource = (attachment: EmailAttachment): AttachmentSource => ({
                    content: attachment.content,
                    data: attachment.data,
                    url: attachment.url,
                    filename: attachment.filename,
                    mimeType: attachment.mimeType,
                    contentType: attachment.contentType,
                    size: attachment.size,
                });

                const normalizedAttachments =
                    Array.isArray(emailData.attachments)
                        ? emailData.attachments.map((attachment: EmailAttachment) => {
                            let filename = attachment.filename || "document";
                            const extension = getFileExtension(attachment.url || "", null);
                            if (extension && !filename.includes(".")) {
                                filename = `${filename}.${extension}`;
                            }
                            const mimeType =
                                attachment.mimeType ||
                                attachment.contentType ||
                                (extension ? getMimeTypeFromExtension(extension) : "application/octet-stream");
                            return {
                                id: attachment.id,
                                filename,
                                mimeType,
                                contentType: mimeType,
                                size: attachment.size,
                                url: attachment.url,
                                content: attachment.content ?? attachment.data,
                            };
                        })
                        : [];

                const attachmentSources = normalizedAttachments.map(mapToAttachmentSource);
                const attachments = await convertAttachmentsToFiles(attachmentSources);

                // 6. Send email to each recipient via Chatwoot
                const results = await Promise.allSettled(
                    toEmails.map(async (recipientEmail: string) => {
                        try {
                            const contactNameForEmail =
                                contactName || getCapitalizedNameFromEmail(recipientEmail);
                            const contactPayload: ContactCreatePayload = {
                                name: contactNameForEmail,
                                email: recipientEmail,
                            };

                            const contact = await contactResolutionService.findOrCreateContact(
                                accountId,
                                contactPayload
                            );

                            if (!contact?.id) {
                                throw new Error(`Failed to find or create contact for ${recipientEmail}`);
                            }
                            const conversationId = await conversationComposerService.composeAndCreate(
                                accountId,
                                {
                                    inboxId: senderInbox.id,
                                    contactId: contact.id,
                                    sourceId: recipientEmail,
                                    message: messageContent,
                                    subject: subject,
                                    ccEmails,
                                    bccEmails,
                                    toEmails: [recipientEmail],
                                    attachments,
                                    channelType: ChannelType.EMAIL,
                                    additionalAttributes: {
                                        gomake_document: {
                                            id: boardMissionId,
                                            type: DOCUMENT_TYPE.boardMission,
                                        },
                                    },
                                }
                            );

                            if (!conversationId) {
                                throw new Error(`Failed to create conversation for ${recipientEmail}`);
                            }
                            return { recipientEmail, success: true, conversationId };
                        } catch (error) {
                            return { recipientEmail, success: false, error: error instanceof Error ? error.message : String(error) };
                        }
                    })
                );

                const successfulSends = results.filter(
                    (result) => result.status === "fulfilled" && result.value.success
                );
                const failedSends = results.filter(
                    (result) => result.status === "rejected" || (result.status === "fulfilled" && !result.value.success)
                );

                return successfulSends.length > 0;
            } catch (error) {
                return false;
            }
        },
        [
            callApi,
            hasFrontDeskFeature,
            selectedAccountId,
            setSelectedAccountId,
            currentUser,
            profileUser,
            fetchProfile,
            inboxes,
        ]
    );

    const onClickMoveBoardMissionToDone = async (isSendMessage: boolean = false) => {
        const callBack = async (res) => {
            closeMoveToReadyModal();
            if (res?.success) {
                alertSuccessUpdate();

                // If isSendMessage is true and hasFrontDeskFeature, send email programmatically without opening modal
                if (isSendMessage && hasFrontDeskFeature) {
                    try {
                        const success = await sendEmailProgrammatically(
                            selectedMission?.id || selectedMission?.boardMissionId,
                            selectedMission?.contactEmail,
                            selectedMission?.contactPhone,
                            selectedMission?.contactName,
                            selectedMission?.customerId,
                            selectedMission?.customerName
                        );
                        if (!success) {
                            console.warn("Failed to send email programmatically, but mission was marked as done");
                        }
                    } catch (error) {
                        console.error("Error sending email programmatically:", error);
                    }
                }

                setOpenPackagesModal(true);
            } else {
                alertFaultUpdate();
            }
        };

        // Check if this is an order action (OrderToReady) or board mission action
        if (selectedMission?.qrCodeAction === QrCodeAction.OrderToReady) {
            await moveOrderToDoneApi(callApi, callBack, {
                orderId: selectedMission.orderId,
                isSendMessage: hasFrontDeskFeature ? false : isSendMessage, // Always false if FrontDesk - we handle email sending programmatically
            });
        } else {
            // Mark as ready - send email programmatically if isSendMessage is true and hasFrontDeskFeature
            await moveBoardMissionToDoneApi(callApi, callBack, {
                boardMissionId: selectedMission?.id || selectedMission?.boardMissionId,
                sendMessage: hasFrontDeskFeature ? false : isSendMessage, // Always false if FrontDesk - we handle email sending programmatically
                productType: "",
                storage: selectedMission?.storage ?? "",
            });
        }
    };

    return (
        <div>
            {/* <div
                style={{ position: "absolute", bottom: 50, left: 50, zIndex: 999999999999, fontSize: 50, fontWeight: 600, color: "var(--theme-primary-color, #2E3092)" }}
                onClick={() => {
                    handleBoardMissionsQRCodeAction("291582-216574-0-3")
                }}
            >Scanner QR Code to proceed
            </div> */}
            <BarcodeReader
                onError={handleError}
                onScan={handleScan}
            />
            {allowManualQrEntry && (
                <Stack
                    sx={{
                        position: "fixed",
                        bottom: 16,
                        left: 16,
                        zIndex: 99999,
                        maxWidth: devManualQrMinimized ? "none" : 480,
                        p: devManualQrMinimized ? 0.75 : 1.5,
                        bgcolor: "background.paper",
                        boxShadow: 3,
                        borderRadius: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    {devManualQrMinimized ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Tooltip
                                title={t("productionFloor.expandDevQrPanel", {
                                    defaultValue: "Expand manual QR panel",
                                })}
                            >
                                <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => setDevManualQrMinimized(false)}
                                    aria-label={t("productionFloor.expandDevQrPanel", {
                                        defaultValue: "Expand manual QR panel",
                                    })}
                                >
                                    <UnfoldMoreIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary">
                                {t("productionFloor.devManualQrCollapsed", {
                                    defaultValue: "Dev: manual QR",
                                })}
                            </Typography>
                        </Stack>
                    ) : (
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ sm: "center" }}
                        >
                            <TextField
                                size="small"
                                fullWidth
                                label="Dev: paste QR / barcode"
                                value={devManualBarcode}
                                onChange={(e) => setDevManualBarcode(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        void submitManualDevBarcode();
                                    }
                                }}
                                inputProps={{ "aria-label": "Dev manual QR or barcode value" }}
                            />
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => void submitManualDevBarcode()}
                                >
                                    {t("modal.ok", { defaultValue: "OK" })}
                                </Button>
                                <Tooltip
                                    title={t("productionFloor.minimizeDevQrPanel", {
                                        defaultValue: "Minimize panel",
                                    })}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => setDevManualQrMinimized(true)}
                                        aria-label={t("productionFloor.minimizeDevQrPanel", {
                                            defaultValue: "Minimize panel",
                                        })}
                                    >
                                        <UnfoldLessIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            )}
            <GoMakeAlertModal
                openModal={isDeliveryModalOpen}
                onClose={() => setIsDeliveryModalOpen(false)}
                title={t("delivery")}
                subTitle={t("productionFloor.deliveryQrCodeError")}
                onClickConfirm={handleModalConfirm}
                yesBtn="modal.ok"
                withCancelBtn={true}
            />
            <GoMakeDeleteModal
                modalType={ModalType.WARNING}
                title={t("boardMissions.markAsReadyJobsModalTitle")}
                yesBtn={t("modal.confirm")}
                openModal={moveToReadyMode === "hasJobs"}
                onClose={closeMoveToReadyModal}
                onClickDelete={() => onClickMoveBoardMissionToDone(false)}
                onClickCancel={closeMoveToReadyModal}
            >
                {renderMoveToReadyStorageField()}
            </GoMakeDeleteModal>
            <GoMakeDeleteModal
                modalType={ModalType.WARNING}
                title={t("boardMissions.markDoneModalTitle")}
                subTitle={t("boardMissions.markDoneModalSubTitle")}
                yesBtn={t("modal.confirm")}
                openModal={moveToReadyMode === "frontDeskConfirm"}
                onClose={closeMoveToReadyModal}
                onClickDelete={() => onClickMoveBoardMissionToDone(false)}
                onClickCancel={closeMoveToReadyModal}
            >
                {renderMoveToReadyStorageField()}
            </GoMakeDeleteModal>
            <ThreeOptionsModal
                modalType={ModalType.WARNING}
                title={t("boardMissions.markDoneModalTitle")}
                subTitle={t("boardMissions.markDoneModalSubTitle")}
                openModal={moveToReadyMode === "withMessage"}
                onClose={closeMoveToReadyModal}
                onClickYes={() => onClickMoveBoardMissionToDone(true)}
                onClickNo={() => onClickMoveBoardMissionToDone(false)}
                yesBtn={t("boardMissions.markDoneModalYes")}
                noBtn={t("boardMissions.markDoneModalNo")}
                style={
                    isMobile
                        ? {
                            width: "95vw",
                            maxWidth: 600,
                            minWidth: "unset",
                        }
                        : undefined
                }
            >
                {renderMoveToReadyStorageField()}
            </ThreeOptionsModal>
            <PrintPackingSlipModalWidget
                openModal={openPackagesModal}
                onClose={() => setOpenPackagesModal(false)}
                boardMissionId={selectedMission?.id || selectedMission?.boardMissionId}
                quantity={selectedJobQuantity}
            />
            <QRErrorModal
                openModal={isQRErrorModalOpen}
                onClose={() => setIsQRErrorModalOpen(false)}
                errorMessage={qrErrorMessage}
                errorKey={qrErrorKey}
            />
            {isEmailComposerOpen && (
                <FrontDeskEmailComposerWrapper
                    open={isEmailComposerOpen}
                    onClose={closeEmailComposer}
                    emailData={emailData}
                    documentId={selectedMission?.id}
                    documentType={DOCUMENT_TYPE.boardMission}
                />
            )}
        </div>
    )
}
export default QrCodeReader;